import { type SiteId, type UserEntity } from '@solinkcorp/api-types';
import { type CallHomeUser, type JWTUser } from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import {
  type CamerasDictionaryCamera,
  type EventFilterState,
  type EventSubtypeFilterState,
  type VideoDiscoverySite,
} from 'utils/types';
import { type VIDEO_DOWNLOAD_TYPES } from 'vd-constants';

export interface VideoContextValues {
  updateTimelineEvents: (events: IEvent[] | undefined) => void;
  syncTimelineEvent: (event: IEvent) => void;
  timelineEvents: IEvent[] | undefined;
  updateLiveToggle: (isLiveToggleOn: boolean) => void;
  isLiveToggleOn: boolean;
}

export const VideoContext = React.createContext<VideoContextValues>({} as VideoContextValues);
VideoContext.displayName = 'VideoContext';

export interface AppState {
  menuOpen: boolean;
  showTabs: boolean;
  topNavZIndex: number;
  showReloadNotice: boolean;
  bannerHeightPx: number;
  setBannerHeightPx: (height: number) => void;
  reloadNotice: 'non-critical' | 'critical' | null;
  /**
   * Milliseconds until the forced reload when the notice is triggered. Null
   * uses the default. A short value lets developers reach the countdown modal
   * quickly.
   */
  reloadNoticeCountdownMs: number | null;
  /**
   * Absolute timestamp (ms) of the pending forced reload, or null when no
   * notice is active. Published by the reload notifier so other views (for
   * example the developer test panel) can show the remaining deferred time.
   */
  reloadNoticeDeadline: number | null;
  setReloadNoticeDeadline: (deadline: number | null) => void;
  showIntegrationBanner: boolean;
  setIsIntegrationBannerVisible: (isVisible: boolean) => void;
  setReloadNotice: (
    notice: 'non-critical' | 'critical' | null,
    countdownMs?: number | null,
  ) => void;
}

export interface CommonContextValues {
  locale: LocaleDefinition;
  locations: {
    filteredLocations: VideoDiscoverySite[];
    loading: boolean;
    masterLocationsListLastUpdate: number;
    restrictions: {
      deviceIds?: string[];
      cameraIds?: string[];
    };
  };
  locationsById?: Record<SiteId, VideoDiscoverySite>;
  authData: any;
  shared: {
    isContextMenuOpen: boolean;
    isModalOpen: boolean;
  };
  masterLocationsList: VideoDiscoverySite[];
  masterRealLocationsList: VideoDiscoverySite[];
  camerasDictionary: { [cameraId: string]: CamerasDictionaryCamera };
  masterTime: number;
  user: JWTUser | undefined;
  updateContextMenuStatus: (params: { isOpen: boolean }) => void;
  appState: AppState;
}

export const CommonContext = React.createContext<CommonContextValues>({} as CommonContextValues);
CommonContext.displayName = 'CommonContext';

interface DigestRun {
  day: number;
  time: string;
}
export interface DigestSettings {
  timeZone?: string;
  schedule?: {
    simple?: {
      daily?: { time?: string; run?: DigestRun[] };
      weekly?: { time?: string; run?: DigestRun[] };
    };
  };
  simple?: {
    daily: { time: string; run: { day: number; time: string }[] };
    weekly: { time: string; run: { day: number; time: string }[] };
    timeZone?: string;
  };
}
export interface UserContextValues {
  current: CallHomeUser | unknown;
  data: UserEntity[];
  digestSettings: DigestSettings;
  hasSMSNumber: boolean;
  isLoaded: boolean;
  loading: boolean;
  locationProcessing: boolean;
  // smsId: null,
  // smsNumber: null,
  impersonating: boolean;
}

export const UsersContext = React.createContext<UserContextValues>({} as UserContextValues);
UsersContext.displayName = 'UsersContext';

export interface EventsContextValues {
  filters: {
    changeDate: number;
    searchCapsules: string[];
    searchString: string;
    searchStringRegex: string;
    selectedFilter: any;
    selectedFilterId: string;
    showRevenue: boolean;
    types: EventFilterState;
    subTypes: EventSubtypeFilterState;
    tags?: string[];
  };
  updateHistogramOpenState: (status: boolean) => void;
  histogramOpen: boolean;
  pinnedEvent: IEvent | undefined;
}

export const EventsContext = React.createContext<EventsContextValues>({} as EventsContextValues);
EventsContext.displayName = 'EventsContext';

interface SettingsReports {
  showThresholdAlert: boolean;
  thresholdTemplateAverage: number;
  thresholdTemplateId: string;
  thresholdType: string;
}

export interface SettingsContextValues {
  isDigestEnabled: boolean;
  resetReportSettings: () => void;
  setReportSettings: (settings: SettingsReports) => void;
  reports: SettingsReports;
  preferences: {
    shouldAllowSupportAccess: boolean;
    shouldShowExperimentalFeatures: boolean;
    shouldShowAllEventThumbnails: boolean;
    selectedDownloadType: (typeof VIDEO_DOWNLOAD_TYPES)[number];
  };
}

export const SettingsContext = React.createContext<SettingsContextValues>(
  {} as SettingsContextValues,
);
SettingsContext.displayName = 'SettingsContext';
